import { Tilemaps } from "phaser";
import { EventBus } from "../../EventBus.ts";
import { Player } from "../../classes/player.ts";
import {
  gameObjectsToObjectPoints,
  mobileAndTabletCheck,
} from "../../helpers/gameobject-to-object-point.ts";
import { DEFAULT_PLAYER_START_LOCATION, EVENTS_NAME, MAP_DIM_VAL } from "../../consts.ts";
import { BaseScene } from "./BaseScene.ts";
import { Mrpas } from "mrpas";
// import ShakePosition from "phaser3-rex-plugins/plugins/shakeposition-plugin.js";
// import PluginManager = Phaser.Plugins.PluginManager;
import { Npc } from "../../classes/npc.ts";
import { Monster } from "../../classes/monster.ts";
import { AccountStruct, SupportMobStruct } from "../../../api.ts";

const DEBUG = false;
interface ObjectPointProperty {
  name: string;
  value: string;
}

export class GameMain extends BaseScene {
  tilesNpc: Tilemaps.Tileset;
  tilesNpc32: Tilemaps.Tileset;
  tilesTerrain: Tilemaps.Tileset;
  tilesCity: Tilemaps.Tileset;
  tilesCityProps: Tilemaps.Tileset;
  tilesBuildings: Tilemaps.Tileset;
  tilesShopping: Tilemaps.Tileset;
  tilesVehicles: Tilemaps.Tileset;
  tilesDrinkMachines: Tilemaps.Tileset;
  npcs: Npc[] = [];
  players: Record<string, Player> = {};
  // Server-authoritative combat monsters, keyed by server_iid.
  monsters: Record<number, Monster> = {};
  // MobControl can land before the mob's first SupportMobInfo — stash it.
  private pendingMobControl: Record<number, boolean> = {};

  constructor() {
    super("GameMain");
    this.mapName = "Main map";
  }

  create(): void {
    this.initMap();
    const account: AccountStruct = JSON.parse(localStorage.getItem("account")!);
    const playerStartLocation =
      account && account.position && account.position.x_pos
        ? [account.position.x_pos, account.position.y_pos]
        : DEFAULT_PLAYER_START_LOCATION;
    this.player1 = new Player(
      this,
      account.account_name,
      account.player_sprite_id || 1,
      playerStartLocation[0],
      playerStartLocation[1],
      account.score,
      account.alcohol,
      account.health,
      true,
    );
    // [15 * 16, 101 * 16]
    // this.npcs.push(new Npc(this, "0a", "0a", 17 * 16, 103 * 16));

    // var shakePosition = new ShakePosition(this as unknown as PluginManager);
    // this.player1.shake = shakePosition
    //   .add(this.player1, {
    //     duration: 100,
    //     magnitude: 1,
    //     mode: "effect",
    //     axis: 0,
    //   })
    //   .on("complete", function () {
    //     console.log("shake complete");
    //   });

    this.fov = new Mrpas(this.map.width, this.map.height, (x, y) => {
      const tile = this.wallsLayer!.getTileAt(x, y);
      return !tile || !tile.collides;
    });
    this.physics.add.collider(this.player1, this.wallsLayer);
    this.initCamera();
    this.initZones();
    this.initResources();
    this.initStorylines();
    this.initChallenges();
    this.initInteractiveChallenges();
    this.initTeleporters();
    this.initNpcNameplates();
    this.initPath();
    this.initNpcs();

    if (mobileAndTabletCheck()) {
      console.log("skipping mobile initialization");
    }
    EventBus.emit(EVENTS_NAME.currentSceneReady, this);
    // A dead scene's listener throws on the destroyed displayList
    // (queueDepthSort on null) and that throw aborts the emit chain, so a
    // LIVE scene's listener registered after it never runs — remote players
    // silently stop spawning. Drop any stale listeners from a previous
    // GameMain first, and unhook on scene shutdown (Phaser never calls a
    // scene's destroy() method — cleanup must ride the SHUTDOWN event).
    EventBus.removeListener(EVENTS_NAME.showGameOutro);
    EventBus.removeListener(EVENTS_NAME.remotePlayerInfo);
    EventBus.removeListener(EVENTS_NAME.entitySnapshot);
    EventBus.removeListener(EVENTS_NAME.removePlayer);
    EventBus.removeListener(EVENTS_NAME.supportMobInfo);
    EventBus.removeListener(EVENTS_NAME.removeSupportMob);
    EventBus.removeListener(EVENTS_NAME.mobControl);
    EventBus.removeListener(EVENTS_NAME.monsterIntroClosed);
    EventBus.on(EVENTS_NAME.showGameOutro, () => {
      this.changeScene();
      EventBus.emit(EVENTS_NAME.showGameOutroContinued);
    });
    EventBus.on(EVENTS_NAME.remotePlayerInfo, (account: AccountStruct) => {
      console.log("received remote player info", account);
      this.updateRemotePlayer(account);
    });
    EventBus.on(
      EVENTS_NAME.entitySnapshot,
      (snap: { players: AccountStruct[]; support_mobs: SupportMobStruct[] }) => {
        this.applyEntitySnapshot(snap);
      },
    );
    EventBus.on(EVENTS_NAME.removePlayer, (accountName: string) => {
      console.log("removing remote player", accountName);
      this.removeRemotePlayer(accountName);
    });
    EventBus.on(EVENTS_NAME.supportMobInfo, (mob: SupportMobStruct) => {
      this.updateSupportMob(mob);
    });
    EventBus.on(EVENTS_NAME.removeSupportMob, (serverIid: number) => {
      this.removeSupportMobSprite(serverIid);
    });
    EventBus.on(
      EVENTS_NAME.mobControl,
      (ctl: { server_iid: number; enabled: boolean }) => {
        const m = this.monsters[ctl.server_iid];
        if (m) {
          m.controlled = ctl.enabled;
        } else {
          // SupportMobInfo for this mob hasn't landed yet — apply on create.
          this.pendingMobControl[ctl.server_iid] = ctl.enabled;
        }
      },
    );
    EventBus.on(EVENTS_NAME.monsterIntroClosed, () => {
      // resume after the first-hostile tutorial
      if (this.scene.isPaused()) {
        this.scene.resume();
      }
    });
    // Swinging at a remote character makes it recoil. Purely local and purely
    // visual — no damage, no message. See Player.flinch: AI mobs arrive
    // through the remote-player path and never take damage, so "I hit them
    // and nothing happened" was a free and perfectly reliable way to spot
    // one. This is on this.game.events (Phaser's own bus, same channel
    // Monster listens on), so it needs the same SHUTDOWN unhook as the
    // EventBus listeners above.
    this.game.events.removeListener(EVENTS_NAME.attack, this.flinchNearby, this);
    this.game.events.on(EVENTS_NAME.attack, this.flinchNearby, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.removeListener(EVENTS_NAME.attack, this.flinchNearby, this);
      EventBus.removeListener(EVENTS_NAME.showGameOutro);
      EventBus.removeListener(EVENTS_NAME.remotePlayerInfo);
      EventBus.removeListener(EVENTS_NAME.entitySnapshot);
      EventBus.removeListener(EVENTS_NAME.removePlayer);
      EventBus.removeListener(EVENTS_NAME.supportMobInfo);
      EventBus.removeListener(EVENTS_NAME.removeSupportMob);
      EventBus.removeListener(EVENTS_NAME.mobControl);
      EventBus.removeListener(EVENTS_NAME.monsterIntroClosed);
      // Scene instances survive scene.restart() — these registries would
      // otherwise carry DESTROYED sprites into the next create() and the
      // update loop would crash touching them (the "scene error on death
      // restart"). Fresh world state arrives from the 1s server snapshot.
      this.players = {};
      this.monsters = {};
      this.pendingMobControl = {};
      this.npcs = [];
    });
    EventBus.emit(EVENTS_NAME.bootedGame);
  }

  /** Local player swung: recoil every remote character in reach. */
  private flinchNearby(): void {
    // Same reach the monsters use for melee, so what you can hit and what
    // reacts are the same set.
    const MELEE_RANGE = 90;
    const me = this.player1;
    if (!me || !this.sys?.displayList) {
      return;
    }
    for (const other of Object.values(this.players)) {
      if (!other || !other.active) {
        continue;
      }
      if (Phaser.Math.Distance.Between(me.x, me.y, other.x, other.y) <= MELEE_RANGE) {
        other.flinch();
      }
    }
  }

  /** Server broadcast landed: create the mob if we've never seen it,
   *  otherwise sync its state. Clients NEVER spawn monsters on their own —
   *  the server owns existence; this is the only way one appears. */
  updateSupportMob(mob: SupportMobStruct): void {
    if (!this.sys || !this.sys.displayList) {
      return;
    }
    const existing = this.monsters[mob.server_iid];
    if (!existing) {
      const monster = new Monster(
        this,
        mob.server_iid,
        mob.tiled_id,
        mob.name,
        mob.position.x_pos,
        mob.position.y_pos,
        mob.health,
        mob.max_health,
      );
      this.monsters[mob.server_iid] = monster;
      this.physics.add.collider(monster, this.wallsLayer);
      // Victim-authoritative touch damage: MY client detects the mob
      // touching MY player and applies/reports it (1s cooldown inside).
      this.physics.add.overlap(this.player1, monster, () => {
        monster.tryTouchLocalPlayer(this.player1);
      });
      const pending = this.pendingMobControl[mob.server_iid];
      if (pending !== undefined) {
        monster.controlled = pending;
        delete this.pendingMobControl[mob.server_iid];
      }
      // First hostile the player has EVER seen: pause and explain the
      // danger + the spacebar/double-tap punch (shown once, like the intro).
      if (localStorage.getItem("hasSeenMonsterIntro") !== "true") {
        localStorage.setItem("hasSeenMonsterIntro", "true");
        this.scene.pause();
        EventBus.emit(EVENTS_NAME.showMonsterIntro);
      }
    } else {
      existing.applyServerState(
        mob.position.x_pos,
        mob.position.y_pos,
        mob.health,
      );
    }
  }

  /** One tick's COMPLETE view of what this player can see (EntitySnapshot).
   *  Fans out into the same per-entity paths as before, then despawns anything
   *  absent — the snapshot is exhaustive, so absence means "left range or
   *  died". That removal signal never existed before: an entity that walked
   *  out of the AOA simply stopped being sent and its sprite lingered at its
   *  last known position forever. */
  applyEntitySnapshot(snap: {
    players: AccountStruct[];
    support_mobs: SupportMobStruct[];
  }): void {
    if (!this.sys || !this.sys.displayList) {
      return;
    }
    const seenPlayers = new Set<string>();
    for (const p of snap?.players ?? []) {
      if (!p?.account_name) continue;
      seenPlayers.add(p.account_name);
      this.updateRemotePlayer(p);
    }
    const seenMobs = new Set<number>();
    for (const m of snap?.support_mobs ?? []) {
      if (m?.server_iid === undefined) continue;
      seenMobs.add(m.server_iid);
      this.updateSupportMob(m);
    }
    for (const name of Object.keys(this.players)) {
      if (!seenPlayers.has(name)) {
        this.removeRemotePlayer(name);
      }
    }
    for (const iid of Object.keys(this.monsters)) {
      if (!seenMobs.has(Number(iid))) {
        // Out of range, not killed — no death pop.
        this.despawnSupportMob(Number(iid));
      }
    }
  }

  /** Silent removal for an entity that left the AOA. Deaths use
   *  removeSupportMobSprite, which plays the death pop — a car that merely
   *  drove out of view must not appear to explode. */
  despawnSupportMob(serverIid: number): void {
    const monster = this.monsters[serverIid];
    if (monster) {
      monster.destroy();
      delete this.monsters[serverIid];
    }
  }

  removeSupportMobSprite(serverIid: number): void {
    if (!this.sys || !this.sys.displayList) {
      return;
    }
    const monster = this.monsters[serverIid];
    if (monster) {
      // little death pop, then gone
      this.tweens.add({
        targets: monster,
        alpha: 0,
        scale: monster.scale * 1.4,
        duration: 250,
        onComplete: () => monster.destroy(),
      });
      delete this.monsters[serverIid];
    }
  }

  removeRemotePlayer(accountName: string): void {
    if (!this.sys || !this.sys.displayList) {
      return;
    }
    const player = this.players[accountName];
    if (player) {
      player.destroy();
      delete this.players[accountName];
    }
  }

  updateRemotePlayer(account: AccountStruct): void {
    // Never touch a scene that's been shut down (its displayList is gone) —
    // constructing a sprite on it throws and kills the whole emit chain.
    if (!this.sys || !this.sys.displayList) {
      return;
    }
    if (!this.players[account.account_name]) {
      console.log("adding remote player", account.account_name);
      const newPlayer = new Player(
        this,
        account.account_name,
        account.player_sprite_id || 1,
        account.position!.x_pos,
        account.position!.y_pos,
        account.score,
        account.alcohol,
        account.health,
        false,
      );
      this.players[account.account_name] = newPlayer;
      this.physics.add.collider(newPlayer, this.wallsLayer);
    } else {
      console.log("updating remote player", account.account_name);
      const existingPlayer = this.players[account.account_name];
      existingPlayer.score = account.score;
      existingPlayer.alcohol = account.alcohol;
      existingPlayer.health = account.health;
      const toX = Math.floor(account.position!.x_pos / MAP_DIM_VAL);
      const toY = Math.floor(account.position!.y_pos / MAP_DIM_VAL);
      const fromX = Math.floor(existingPlayer.x / MAP_DIM_VAL);
      const fromY = Math.floor(existingPlayer.y / MAP_DIM_VAL);
      const WIGGLE_ROOM = 5; // pixels wiggle room
      if (
        Math.abs(fromX - toX) * MAP_DIM_VAL > WIGGLE_ROOM ||
        Math.abs(fromY - toY) * MAP_DIM_VAL > WIGGLE_ROOM
      ) {
        console.log("executing a path", fromX, fromY, toX, toY);
        try {
          this.easystar.findPath(fromX, fromY, toX, toY, (path) => {
            console.log("WTF");
            if (path === null) {
              console.log("Path was not found.");
            } else {
              console.log("Path was found.");
              this.moveCharacter(existingPlayer, path);
            }
          });
          this.easystar.calculate();
        } catch (error) {
          console.error("Error calculating path (ignored):", error);
        }
      } else {
        console.log(
          "Failed to execute a path",
          Math.abs(fromX - toX) * MAP_DIM_VAL,
          Math.abs(fromY - toY) * MAP_DIM_VAL,
        );
      }
      existingPlayer.update();
    }
  }

  update(): void {
    this.globalUpdate();
    Object.values(this.players).forEach((player) => {
      // .active guards against a just-destroyed sprite (despawn same tick)
      if (player.active) {
        player.update();
      }
    });
  }

  changeScene() {
    this.scene.stop("GameUIScene");
    this.scene.stop("SoundTrackScene");
    this.scene.start("GameWonScene");
  }

  initNpcs(): void {
    const npcs = gameObjectsToObjectPoints(
      this.map.filterObjects("npc_spawner", (obj) => obj.name === "NPC"),
    );
    npcs.map((npc) => {
      const properties: Record<string, string> = {};
      npc.properties.forEach((property: ObjectPointProperty) => {
        properties[property.name] = property.value;
      });
      const localNpc = new Npc(
        this,
        `${properties.dir}-${properties.i}`,
        properties.name,
        npc.x,
        npc.y,
      );
      this.npcs.push(localNpc);
      return null;
    });
  }

  initTeleporters(): void {
    const teleporters = gameObjectsToObjectPoints(
      this.map.filterObjects("Zones", (obj) => obj.name === "Teleporter"),
    );
    const teleporterList = teleporters.map((teleporter) => {
      return this.physics.add
        .sprite(
          teleporter.x + teleporter.width / 2,
          teleporter.y + teleporter.height / 2,
          "tilesMob",
        )
        .setName(
          // Look up the destination "x,y" BY NAME (order-independent).
          String(
            (teleporter.properties || []).find(
              (p: { name: string; value: unknown }) => p.name === "coords",
            )?.value ?? "",
          ),
        )
        .setSize(teleporter.width, teleporter.height)
        .setAlpha(0);
    });
    teleporterList.forEach((teleporter) => {
      this.physics.add.overlap(this.player1, teleporter, (_obj1, _obj2) => {
        const [x, y] = teleporter.name.split(",").map(Number);
        console.log(`teleporting to ${x}, ${y}}`);
        // stop any existing path movement and stop the player
        if (this.player1.moveTweenChain) {
          this.player1.moveTweenChain.stop();
          this.player1.moveTweenChain = null;
        }
        this.player1.setVelocity(0);
        this.player1.x = x;
        this.player1.y = y;
      });
    });
  }

  initMap(): void {
    this.map = this.make.tilemap({
      key: "game26",
      tileWidth: 32,
      tileHeight: 32,
    });

    const ltilesNpc = this.map.addTilesetImage("NPCs", "tilesNpc");
    if (ltilesNpc) {
      this.tilesNpc = ltilesNpc;
    }
    const ltilesNpc32 = this.map.addTilesetImage("32bit NPC", "tilesNpc32");
    if (ltilesNpc32) {
      this.tilesNpc32 = ltilesNpc32;
    }
    const ltilesTerrain = this.map.addTilesetImage(
      "Terrains_and_Fences",
      "tilesTerrain",
    );
    if (ltilesTerrain) {
      this.tilesTerrain = ltilesTerrain;
    }
    const ltilesCity = this.map.addTilesetImage(
      "City_Terrains",
      "tilesCity",
    );
    if (ltilesCity) {
      this.tilesCity = ltilesCity;
    }
    const ltilesCityProps = this.map.addTilesetImage(
        "City_Props",
        "tilesCityProps",
    );
    if (ltilesCityProps) {
      this.tilesCityProps = ltilesCityProps;
    }
    const ltilesBuildings = this.map.addTilesetImage(
        "Generic_Buildings",
        "tilesBuildings",
    );
    if (ltilesBuildings) {
      this.tilesBuildings = ltilesBuildings;
    }
    const ltilesShopping = this.map.addTilesetImage(
        "Shopping_Center",
        "tilesShopping",
    );
    if (ltilesShopping) {
      this.tilesShopping = ltilesShopping;
    }
    const ltilesVehicles = this.map.addTilesetImage(
        "Vehicles",
        "tilesVehicles",
    );
    if (ltilesVehicles) {
      this.tilesVehicles = ltilesVehicles;
    }
    const ltilesDrinkMachines = this.map.addTilesetImage(
        "DrinkMachines",
        "tilesDrinkMachines",
    );
    if (ltilesDrinkMachines) {
      this.tilesDrinkMachines = ltilesDrinkMachines;
    }

    // ── BOOT DIAGNOSTIC ───────────────────────────────────────────────
    // A stale cached map is invisible until an entire district renders
    // black, so state plainly which map actually loaded and whether every
    // tileset this code binds is present in it. Cheap, and it turns a
    // mystified "the game is broken" into a one-line answer.
    const declared = this.map.tilesets.map((t) => t.name);
    const required = [
      "NPCs", "32bit NPC", "Terrains_and_Fences", "City_Terrains",
      "City_Props", "Generic_Buildings", "Shopping_Center",
      "Vehicles", "DrinkMachines",
    ];
    const missing = required.filter((n) => !declared.includes(n));
    console.info(
      `[map] build=${process.env.REACT_APP_BUILD_ID || "dev"} ` +
        `tilesets=${declared.length}/${required.length} [${declared.join(", ")}]`,
    );
    if (missing.length) {
      console.error(
        `[map] STALE OR WRONG MAP -- missing tileset(s): ${missing.join(", ")}. ` +
          `Every district drawn with them will render BLACK. This is almost ` +
          `always a cached map: hard-reload (Cmd+Shift+R) to clear it.`,
      );
    }

    const tilesets = [
      this.tilesNpc,
      this.tilesNpc32,
      this.tilesTerrain,
      this.tilesCity,
      this.tilesCityProps,
      this.tilesBuildings,
      this.tilesShopping,
      this.tilesVehicles,
      this.tilesDrinkMachines,
    ];

    const lgroundLayer = this.map.createLayer("Ground", tilesets, 0, 0);
    if (lgroundLayer) {
      this.groundLayer = lgroundLayer;
    }
    const lwallsLayer = this.map.createLayer("Walls", tilesets, 0, 0);
    if (lwallsLayer) {
      this.wallsLayer = lwallsLayer;
    }
    const loverlayLayer = this.map.createLayer("Overlay", tilesets, 0, 0);
    if (loverlayLayer) {
      this.overlayLayer = loverlayLayer;
    }
    const loverlayLayer2 = this.map.createLayer("Overlay2", tilesets, 0, 0);
    if (loverlayLayer2) {
      this.overlayLayer2 = loverlayLayer2;
    }
    const lvehiclesLayer = this.map.createLayer("Vehicles", tilesets, 0, 0);
    if (lvehiclesLayer) {
      this.vehiclesLayer = lvehiclesLayer;
    }
    const lnpcLayer = this.map.createLayer("NPCs", tilesets, 0, 0);
    if (lnpcLayer) {
      this.npcLayer = lnpcLayer;
    }
    //this.wallsLayer.visible = false;
    this.map.setCollisionByExclusion([0], true, false, this.wallsLayer);
    // this.map.setCollisionByExclusion([0], true, false, this.furnishingLayer);
    if (DEBUG) {
      this.showDebugWalls();
    }
    this.physics.world.setBounds(
      0,
      0,
      this.wallsLayer.width,
      this.wallsLayer.height,
    );
  }

  destroy(): void {
    EventBus.removeListener(EVENTS_NAME.showGameOutro);
    EventBus.removeListener(EVENTS_NAME.remotePlayerInfo);
  }

  private showDebugWalls(): void {
    const debugGraphics = this.add.graphics().setAlpha(0.7);
    this.wallsLayer.renderDebug(debugGraphics, {
      tileColor: null,
      collidingTileColor: new Phaser.Display.Color(243, 234, 48, 255),
    });
  }

  applyGlitchLevel(level: number): void {
    // Sample escalating effects by level; you can customize or expand
    // console.log("applyGlitchLevel", level);
    switch (level) {
      case 1:
        this.cameras.main.shake(500, 0.002);
        break;
      case 3:
        this.cameras.main.flash(300, 255, 0, 0);
        break;
      case 5:
        this.tweens.add({
          targets: this.children.list,
          alpha: 0.5,
          yoyo: true,
          duration: 300,
          repeat: 3,
        });
        break;
      case 8:
        if (this.input && this.input.mouse) {
          this.input.mouse.disableContextMenu();
        }
        break;
      case 10:
        this.cameras.main.zoomTo(0.7, 1000);
        break;
      case 12:
        this.add.rectangle(
          Phaser.Math.Between(0, this.scale.width),
          Phaser.Math.Between(0, this.scale.height),
          50,
          50,
          0xff0000,
          0.7,
        );
        break;
      case 14:
        const container = this.add.container(0, 0, this.children.list);
        this.children.removeAll();
        this.add.existing(container);
        this.tweens.add({
          targets: container,
          angle: 5,
          yoyo: true,
          repeat: -1,
          duration: 500,
        });
        break;
    }
  }
}
